
import React, { useRef, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface FormatOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface CodePreviewProps {
  content: string;
  format: string;
  formatOptions: FormatOption[];
  isStreaming?: boolean;
}

const CodePreview: React.FC<CodePreviewProps> = ({ content, format, formatOptions, isStreaming = false }) => {
  const preRef = useRef<HTMLPreElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom when streaming
  useEffect(() => {
    if (containerRef.current && isStreaming) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [content, isStreaming]);
  
  // Debug logging
  useEffect(() => {
    if (isStreaming) {
      console.log("CodePreview: Streaming mode active, content length:", content?.length || 0);
    }
  }, [isStreaming, content]);
  
  if (!content && !isStreaming) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">No code content yet</p>
          <p className="text-gray-400 text-sm mt-1">Generate content with the AI assistant</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {isStreaming && (
        <div className="bg-gray-800 text-jade px-4 py-2 flex items-center border-b border-jade/20">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          <span className="text-sm font-semibold">Template wird generiert...</span>
          <span className="text-xs ml-2 text-gray-400">Bitte warten, während der HTML-Code erstellt wird</span>
        </div>
      )}
      <div 
        ref={containerRef}
        className="flex-1 overflow-auto font-mono text-sm bg-gray-900 text-gray-100"
      >
        {isStreaming && !content ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-jade mb-4" />
            <p className="text-jade font-medium">E-Mail-Template wird generiert...</p>
            <p className="text-gray-400 text-sm mt-2 max-w-md">
              Der generierte HTML-Code wird hier erscheinen. Der KI-Assistent erstellt ein professionelles Template basierend auf dem bestätigten Konzept.
            </p>
            <div className="mt-6 flex space-x-1">
              <div className="w-2 h-2 bg-jade rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-jade rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-jade rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        ) : (
          <SyntaxHighlighter 
            language="html" 
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              borderRadius: 0,
              minHeight: '100%',
              backgroundColor: 'transparent'
            }}
            wrapLines={true}
            lineProps={{style: {wordBreak: 'break-all', whiteSpace: 'pre-wrap'}}}
          >
            {content || ' '}
          </SyntaxHighlighter>
        )}
      </div>
    </div>
  );
};

export default CodePreview;
